import React from 'react';
type Tome = {
  number: string;
  title: string;
  sessions: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Arrive & Connect: Safety, Rhythm and Trust for New and Ever-Changing Groups',
  sessions: 'Sessions S01–S09',
  promise: 'First sessions, open-door groups and short stays: a structure that holds when people come and go.',
  badge: 'bg-court text-white',
  contents: ['The Energy Dial: read the room in 60 seconds', 'Name & Gesture Circle: everyone seen, no one put on the spot', 'Pulse Circle: a shared beat for groups that will not talk', 'Welcome-Anytime Session for people who arrive mid-group', 'The Opt-In Ladder: watch, small move, full move', 'Mixed-Ability Circle: standing, seated and wheelchairs at once']
}, {
  number: '02',
  title: 'Grounded Bodies: Breath, Weight and Presence for Groups That Need to Settle',
  sessions: 'Sessions S10–S18',
  promise: 'For restless, on-edge or scattered rooms: settle the group through feet, breath and weight.',
  badge: 'bg-green-deep text-white',
  contents: ['The Seated Swap: every move standing, seated and in a wheelchair', 'Roots & Weight: settle a restless room through the floor', 'Wave Breath: breath-led movement without overwhelming instructions', 'Look, Locate, Land: orienting for groups that feel on edge', 'The Head-to-Toe Map: one warm-up order for any group', 'Hands Speak: a hands-only session for bedside and low-mobility groups']
}, {
  number: '03',
  title: 'Expression in Motion: Feelings, Shapes and Stories Without Forcing Words',
  sessions: 'Sessions S19–S27',
  promise: 'For groups that resist “feelings talk”: shapes, qualities and images first, words only if they want them.',
  badge: 'bg-sky-deep text-white',
  contents: ['The Movement Quality Palette in plain language', 'Light & Strong, Quick & Slow, Flow & Hold', 'Weather Inside: safe metaphors for inner states', 'Sculpt & Shape: group statues that say what words cannot', 'The Shape-Word Bridge: from movement to a few words, never forced', 'Processing questions that do not interpret for the client']
}, {
  number: '04',
  title: 'Relationship & Connection: Mirroring, Leading, Following and Boundaries',
  sessions: 'Sessions S28–S36',
  promise: 'Connection in steps, from side by side to mirroring, with boundaries practised as much as closeness.',
  badge: 'bg-night text-white',
  contents: ['The Mirror Ladder: side by side, follow, mirror, dialogue', 'Side by Side: for groups not ready for eye contact', 'Flock: move as one group with no one in charge', 'The Space Bubble Scale: distance always chosen by the participant', 'Yes, No, Maybe: movement signals for consent and choice', 'No-touch versions of every partner exercise']
}, {
  number: '05',
  title: 'Energy, Play & Strength: Rhythm and Play for Low-Energy and High-Energy Rooms',
  sessions: 'Sessions S37–S45',
  promise: 'Lift a flat room or channel a loud one, with play that adults never find childish.',
  badge: 'bg-shuttle text-ink',
  contents: ['The Rhythm Builder: from one sound to a group score', 'Chair Drum Circle with chairs, laps and hands', 'The Play Thermostat: dial play up or down', 'Invisible Ball and Group Machine for groups that “don’t do play”', 'Strong Shapes: taking up space, standing or seated', 'The Dance Builder: a dance the group owns, in four layers']
}, {
  number: '06',
  title: 'Seasons, Transitions & Endings: Themed Sessions, Milestones and Goodbyes',
  sessions: 'Sessions S46–S54',
  promise: 'Beginnings, discharges, new terms and last sessions: endings planned with care.',
  badge: 'bg-court-deep text-white',
  contents: ['The Year Map for your own setting’s calendar', 'New Beginnings, Gathering and Rest Season', 'Doors & Thresholds for discharge, a new term or a new home', 'Carry & Leave: what I take with me, what I set down', 'The Closing Circle Formula: a five-minute ending ritual', 'The Last Dance, plus 10 goodbye rituals']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-white" data-id="element-336" data-mp="3uy3ld37042jm">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-337" data-mp="3v06jybm7z3zy">
        <div className="text-center" data-id="element-338" data-mp="3pyh84tr6amat">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-court-deep" data-id="element-339" data-mp="3q1muw2cbxc3d">
            The Group Year
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-bold text-ink text-[28px] leading-tight tracking-tight sm:text-5xl" data-id="element-340" data-mp="3rj0k17jhqv1">
            Inside the pack: six volumes, a full year of groups
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-341" data-mp="25l9xeq8w4uuf">
            The volumes follow the life of a group: safety first, then settling
            into the body, expression, relationship, energy, and finally change
            and goodbyes. Every session also works on its own.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-342" data-mp="gaocrqd0bay2">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-2xl border border-line bg-white p-6 shadow-card" data-id="element-343" data-mp="2b550yo3y1ma">
              <div className="flex items-center gap-2.5" data-id="element-344" data-mp="6dtbx0t9gygm">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg font-display text-base font-bold ${tome.badge}`} data-id="element-345" data-mp="3oiwkwwmclumb">
                  {tome.number}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink-muted" data-id="element-346" data-mp="1mr9rypiwbpn7">
                  Volume {tome.number} · {tome.sessions}
                </span>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold leading-snug text-ink" data-id="element-347" data-mp="1qa8yheywpe1m">
                {tome.title}
              </h3>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-348" data-mp="1gai217qscoyw">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-line pt-4" data-id="element-349" data-mp="1gaqfb0qtlwam">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-350" data-mp="28z5t5jy7u8kd">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-court" data-id="element-351" data-mp="2918gtxbwbplx" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}